import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useDashboardData } from '../hooks/useDashboardData';
import KpiCard from '../components/dashboard/KpiCard';
import StepEfficiencyChart from '../components/dashboard/StepEfficiencyChart';
import UserStatsTable from '../components/dashboard/UserStatsTable';
import AiFunnelChart from '../components/dashboard/AiFunnelChart';
import DiagramDetailTable from '../components/dashboard/DiagramDetailTable';
import IdlePerStepChart from '../components/dashboard/IdlePerStepChart';
import ServiceLatencyChart from '../components/dashboard/ServiceLatencyChart';
import CompletionTimeByUserTable from '../components/dashboard/CompletionTimeByUserTable';

export default function DashboardPage() {
  const { data, loading, error, refresh } = useDashboardData();
  const navigate = useNavigate();

  const activeUsers7d = data?.user_stats_7d ? data.user_stats_7d.filter((r) => r.active_minutes > 0) : [];
  const activeUsers30d = data?.user_stats_30d ? data.user_stats_30d.filter((r) => r.active_minutes > 0) : [];
  const avgActiveMinutesPerActiveUser7d = activeUsers7d.length
    ? activeUsers7d.reduce((sum, r) => sum + r.active_minutes, 0) / activeUsers7d.length
    : 0;
  const avgActiveMinutesPerActiveUser30d = activeUsers30d.length
    ? activeUsers30d.reduce((sum, r) => sum + r.active_minutes, 0) / activeUsers30d.length
    : 0;

  return (
    <div style={pageStyle}>
      {/* Header */}
      <div style={headerStyle}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <button onClick={() => navigate('/')} style={backBtnStyle} title="Back to app">
            &#8592;
          </button>
          <h1 style={titleStyle}>PNID Analytics Dashboard</h1>
        </div>
        <button onClick={refresh} disabled={loading} style={refreshBtnStyle}>
          {loading ? 'Refreshing...' : 'Refresh'}
        </button>
      </div>

      {/* Error state */}
      {error && (
        <div style={errorStyle}>
          Failed to load dashboard: {error}
        </div>
      )}

      {/* Loading state */}
      {loading && !data && (
        <div style={loadingStyle}>Loading dashboard data...</div>
      )}

      {/* Dashboard content */}
      {data && (
        <div style={contentStyle}>
          {/* KPI Cards Row */}
          <div style={kpiRowStyle}>
            {/* Diagrams by Status */}
            {data.diagrams_by_status ? (
              <KpiCard
                title="Diagrams by Status"
                items={[
                  { label: 'Not Started', value: data.diagrams_by_status.current.not_started },
                  { label: 'In Progress', value: data.diagrams_by_status.current.in_progress },
                  { label: 'QC', value: data.diagrams_by_status.current.qc },
                ]}
                sparkline={data.diagrams_by_status.sparkline}
                sparklineColor="#6366f1"
              />
            ) : (
              <KpiCard title="Diagrams by Status" items={[{ label: 'Unavailable', value: '-' }]} />
            )}

            {/* Avg Time per Diagram */}
            {data.avg_time ? (
              <KpiCard
                title="Avg Time per Diagram"
                items={[{ label: 'Minutes', value: data.avg_time.avg_minutes }]}
                sparkline={data.avg_time.sparkline}
                sparklineColor="#10b981"
                subtitle={`${data.avg_time.active_pct}% active / ${data.avg_time.idle_pct}% idle`}
              />
            ) : (
              <KpiCard title="Avg Time per Diagram" items={[{ label: 'Unavailable', value: '-' }]} />
            )}

            {/* Tool Usage Counts */}
            {data.tool_usage ? (
              <KpiCard
                title="Tool Usage (7d)"
                items={[
                  {
                    label: 'SAM',
                    value: data.tool_usage.current.sam_uses,
                    delta: data.tool_usage.current.sam_uses - data.tool_usage.previous.sam_uses,
                  },
                  {
                    label: 'Templates',
                    value: data.tool_usage.current.template_runs,
                    delta: data.tool_usage.current.template_runs - data.tool_usage.previous.template_runs,
                  },
                  {
                    label: 'Diagrams',
                    value: data.tool_usage.current.diagrams_opened,
                    delta: data.tool_usage.current.diagrams_opened - data.tool_usage.previous.diagrams_opened,
                  },
                  {
                    label: 'Items',
                    value: data.tool_usage.current.items_marked,
                    delta: data.tool_usage.current.items_marked - data.tool_usage.previous.items_marked,
                  },
                ]}
                sparkline={data.tool_usage.sparkline}
                sparklineColor="#f59e0b"
              />
            ) : (
              <KpiCard title="Tool Usage (7d)" items={[{ label: 'Unavailable', value: '-' }]} />
            )}
          </div>

          {/* KPI Row: Completion / Time / Throughput */}
          <div style={kpiRowStyle}>
            {/* PNIDs Completed */}
            {data.pnid_completed_kpi ? (
              <KpiCard
                title="PNIDs Completed (7d)"
                items={[
                  {
                    label: 'Completed',
                    value: data.pnid_completed_kpi.completed_7d,
                    delta: data.pnid_completed_kpi.completed_7d - data.pnid_completed_kpi.completed_previous_7d,
                  },
                  {
                    label: 'Avg / Week (30d)',
                    value: data.pnid_completed_kpi.avg_weekly_30d,
                  },
                ]}
                sparkline={data.pnid_completed_kpi.sparkline}
                sparklineColor="#4f46e5"
                subtitle="Completion = marked_for_qc or export_triggered"
              />
            ) : (
              <KpiCard title="PNIDs Completed (7d)" items={[{ label: 'Unavailable', value: '-' }]} />
            )}

            {/* Avg Time to Complete */}
            {data.pnid_completion_time_kpi ? (
              <KpiCard
                title="Avg Time to Complete"
                items={[
                  { label: 'Avg (30d, min)', value: data.pnid_completion_time_kpi.avg_minutes_30d },
                  { label: 'Avg (7d, min)', value: data.pnid_completion_time_kpi.avg_minutes_7d },
                ]}
                subtitle="Open → completion (QC/export)"
                sparklineColor="#10b981"
              />
            ) : (
              <KpiCard title="Avg Time to Complete" items={[{ label: 'Unavailable', value: '-' }]} />
            )}

            {/* Final throughput metric */}
            {data.throughput_kpi ? (
              <KpiCard
                title="Final Metric: Throughput"
                items={[
                  {
                    label: 'PNIDs / Active Hour',
                    value: data.throughput_kpi.pnids_per_active_hour.toFixed(2),
                  },
                  {
                    label: 'Active Minutes (7d)',
                    value: data.throughput_kpi.total_active_minutes_7d,
                  },
                ]}
                subtitle="Active minutes use 10m inactivity window"
              />
            ) : (
              <KpiCard title="Final Metric: Throughput" items={[{ label: 'Unavailable', value: '-' }]} />
            )}
          </div>

          {/* KPI Row: Active time summary */}
          <div style={kpiRowStyle}>
            <KpiCard
              title="Avg Active Minutes / User"
              items={[
                { label: '7d', value: avgActiveMinutesPerActiveUser7d.toFixed(1) },
                { label: '30d', value: avgActiveMinutesPerActiveUser30d.toFixed(1) },
              ]}
              subtitle="Based on 10m inactivity window"
            />
          </div>

          {/* Charts and Tables */}
          <StepEfficiencyChart data={data.step_efficiency} />

          <UserStatsTable
            initialData7d={data.user_stats_7d}
            initialData30d={data.user_stats_30d}
          />

          <CompletionTimeByUserTable data={data.completion_time_by_user} />

          <AiFunnelChart data={data.ai_funnel} />

          <DiagramDetailTable data={data.diagram_detail} />

          <div style={twoColStyle}>
            <IdlePerStepChart data={data.idle_per_step} />
            <ServiceLatencyChart data={data.service_latencies} />
          </div>
        </div>
      )}
    </div>
  );
}

// ── Styles ──

const pageStyle: React.CSSProperties = {
  minHeight: '100%',
  backgroundColor: '#f9fafb',
  fontFamily: 'system-ui, -apple-system, sans-serif',
};

const headerStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  padding: '16px 32px',
  backgroundColor: '#fff',
  borderBottom: '1px solid #e5e7eb',
  position: 'sticky',
  top: 0,
  zIndex: 10,
};

const backBtnStyle: React.CSSProperties = {
  background: 'none',
  border: 'none',
  fontSize: 20,
  cursor: 'pointer',
  color: '#6b7280',
  padding: '4px 8px',
  borderRadius: 6,
};

const titleStyle: React.CSSProperties = {
  fontSize: 20,
  fontWeight: 700,
  color: '#111827',
  margin: 0,
};

const refreshBtnStyle: React.CSSProperties = {
  padding: '8px 16px',
  fontSize: 13,
  fontWeight: 600,
  borderRadius: 8,
  border: '1px solid #e5e7eb',
  backgroundColor: '#fff',
  color: '#374151',
  cursor: 'pointer',
};

const errorStyle: React.CSSProperties = {
  margin: '24px 32px',
  padding: '12px 16px',
  backgroundColor: '#fef2f2',
  color: '#dc2626',
  borderRadius: 8,
  fontSize: 14,
};

const loadingStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: 80,
  color: '#9ca3af',
  fontSize: 16,
};

const contentStyle: React.CSSProperties = {
  padding: '24px 32px',
  display: 'flex',
  flexDirection: 'column',
  gap: 24,
  maxWidth: 1280,
  margin: '0 auto',
};

const kpiRowStyle: React.CSSProperties = {
  display: 'flex',
  gap: 20,
  flexWrap: 'wrap',
};

const twoColStyle: React.CSSProperties = {
  display: 'grid',
  gridTemplateColumns: '1fr 1fr',
  gap: 24,
};
